/**
 * Page Driver Step Component
 *
 * Renders the Page Driver configuration step within the Page Setup Wizard.
 * A page can configure driver defaults for more than one dataset source (Galaxy, HCTR, …); each
 * configured dataset gets its own tab. The trailing "+ Add Source" tab (shown only when the deck
 * permits datasets the page hasn't configured yet) opens a menu of the remaining sources. Galaxy is
 * the default source and cannot be removed; any additional source can be deleted via the link below
 * its driver display.
 */

import './PageDriverStep.scss';

import { DATASET_DEFAULT_PAGE_DRIVERS } from '@constants/page';
import { useAurigaContext } from '@context/AurigaContext';
import { usePageSetupContext } from '@context/PageSetupContext';
import { KPIDriversDisplay } from '@deckManagement/components/Common/KPIDriversDisplay';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { DatasetSource, GalaxyKPIDrivers } from '@services/api/deckManagement/graphql/schema/API';
import { resolveDatasetSourceLabel } from '@utils/enumLabelHelpers';
import { generateDriverDisplayOptions } from '@utils/kpiDriverHelpers';
import { isNil } from 'lodash';
import { MouseEvent, SyntheticEvent, useCallback, useMemo, useState } from 'react';
import { KpiDimension } from 'types/PageSidePanel.types';

const baseClassName = 'page-driver-step';
// Sentinel Tab value for the "+ Add Source" affordance — never a real selected dataset.
const ADD_SOURCE_TAB = '__add_source__';

const a11yProps = (id: DatasetSource) => ({
  id: `kpidrivers-tab-${id}`,
  'aria-controls': `kpidrivers-panel-${id}`,
});

const PageDriverStep = () => {
  const { pageDriverSets, setPageDriverSets, enabledDatasets, decklineDatasetCounts, pageError } =
    usePageSetupContext();
  const { toastNotification } = useAurigaContext();

  const [activeDataset, setActiveDataset] = useState<DatasetSource>(DatasetSource.GALAXY);
  const [addMenuAnchor, setAddMenuAnchor] = useState<HTMLElement | null>(null);

  /** Datasets configured on the page, ordered by the deck's enabled order (any extras appended). */
  const configuredDatasets = useMemo<DatasetSource[]>(() => {
    const present = (Object.keys(pageDriverSets) as DatasetSource[]).filter((ds) => !isNil(pageDriverSets[ds]));
    const ordered = enabledDatasets.filter((ds) => present.includes(ds));
    const extras = present.filter((ds) => !enabledDatasets.includes(ds));
    return [...ordered, ...extras];
  }, [enabledDatasets, pageDriverSets]);

  /** Enabled datasets not yet configured — choices for the "+ Add Source" menu. */
  const addableDatasets = useMemo<DatasetSource[]>(
    () => enabledDatasets.filter((ds) => isNil(pageDriverSets[ds])),
    [enabledDatasets, pageDriverSets],
  );

  // If the active dataset was removed (or isn't configured), fall back to the first configured source.
  const currentDataset = configuredDatasets.includes(activeDataset) ? activeDataset : configuredDatasets[0];

  /**
   * DriverDisplayOptionMap per configured dataset. Every configured source keeps its own
   * KPIDriversDisplay mounted (see the panel list below) so switching tabs never remounts it and
   * re-fires its network calls; each mounted panel therefore needs options scoped to its own dataset
   * rather than a single set scoped to the active tab.
   */
  const driverOptionsByDataset = useMemo(
    () =>
      configuredDatasets.reduce(
        (acc, dataset) => {
          acc[dataset] = generateDriverDisplayOptions(pageDriverSets[dataset] ?? DATASET_DEFAULT_PAGE_DRIVERS[dataset]);
          return acc;
        },
        {} as Record<DatasetSource, ReturnType<typeof generateDriverDisplayOptions>>,
      ),
    [configuredDatasets, pageDriverSets],
  );

  /** Builds a change handler that routes KPIDriversDisplay edits to the given dataset's driver set. */
  const handleChangeFor = useCallback(
    (dataset: DatasetSource) => (_key: KpiDimension, updates: Partial<GalaxyKPIDrivers>) => {
      setPageDriverSets((prev) => ({
        ...prev,
        [dataset]: { ...(prev[dataset] ?? DATASET_DEFAULT_PAGE_DRIVERS[dataset]), ...updates },
      }));
    },
    [setPageDriverSets],
  );

  /** Switches tabs; the "+ Add Source" sentinel does not change the selected dataset */
  const handleTabChange = useCallback((_event: SyntheticEvent, value: string) => {
    if (value !== ADD_SOURCE_TAB) {
      setActiveDataset(value as DatasetSource);
    }
  }, []);

  const openAddMenu = useCallback((event: MouseEvent<HTMLElement>) => setAddMenuAnchor(event.currentTarget), []);
  const closeAddMenu = useCallback(() => setAddMenuAnchor(null), []);

  /** Adds the chosen dataset source (seeded with its default drivers) and switches focus to it */
  const handleAddSource = useCallback(
    (dataset: DatasetSource) => {
      setPageDriverSets((prev) =>
        prev[dataset] ? prev : { ...prev, [dataset]: DATASET_DEFAULT_PAGE_DRIVERS[dataset] },
      );
      setActiveDataset(dataset);
      closeAddMenu();
    },
    [setPageDriverSets, closeAddMenu],
  );

  // A source can be deleted as long as it is not the last one — a page must keep at least one dataset.
  const canDeleteSource = configuredDatasets.length > 1;

  /**
   * Removes the given dataset source; if it was active, moves focus to the first remaining source.
   * Blocked (with a toast) when decklines on the page still depend on the dataset — those decklines
   * would be orphaned, so the source must stay until they are reassigned or removed.
   */
  const handleDeleteSource = useCallback(
    (dataset: DatasetSource) => {
      if (configuredDatasets.length <= 1) return;

      const dependentCount = decklineDatasetCounts[dataset] ?? 0;
      if (dependentCount > 0) {
        toastNotification({
          message: `Can't remove the ${resolveDatasetSourceLabel(dataset)} source: ${dependentCount} deckline${
            dependentCount === 1 ? '' : 's'
          } on this page still use ${dependentCount === 1 ? 'it' : 'them'}. Reassign or remove ${
            dependentCount === 1 ? 'that deckline' : 'those decklines'
          } first.`,
          type: 'error',
          visible: true,
        });
        return;
      }

      setPageDriverSets((prev) => {
        const next = { ...prev };
        delete next[dataset];
        return next;
      });
      setActiveDataset((prev) =>
        prev === dataset ? (configuredDatasets.find((ds) => ds !== dataset) ?? dataset) : prev,
      );
    },
    [configuredDatasets, decklineDatasetCounts, setPageDriverSets, toastNotification],
  );

  /** If there is an error, display it instead of the form */
  if (pageError) {
    return <Alert severity='error'>{pageError}</Alert>;
  }

  return (
    <Box className={baseClassName}>
      <Tabs value={currentDataset} onChange={handleTabChange} className={`${baseClassName}__tabs`}>
        {configuredDatasets.map((dataset) => (
          <Tab
            className={`${baseClassName}__tab-item`}
            key={dataset}
            value={dataset}
            label={resolveDatasetSourceLabel(dataset)}
            {...a11yProps(dataset)}
          />
        ))}
        {addableDatasets.length > 0 && (
          // Label-only (no `icon`): a Tab with an icon takes MUI's taller `labelIcon` variant, which
          // would stretch the whole tab row's height.
          <Tab
            className={`${baseClassName}__tab-item`}
            key={ADD_SOURCE_TAB}
            value={ADD_SOURCE_TAB}
            label='+ Add Source'
            onClick={openAddMenu}
          />
        )}
      </Tabs>

      {configuredDatasets.map((dataset) => (
        // Every configured source stays mounted; `hidden` (not conditional rendering) toggles which is
        // shown, so tab switches never unmount KPIDriversDisplay and re-trigger its network calls.
        <Box
          key={`kpidrivers-panel-${dataset}`}
          id={`kpidrivers-panel-${dataset}`}
          role='tabpanel'
          hidden={dataset !== currentDataset}
          aria-labelledby={`kpidrivers-tab-${dataset}`}
        >
          <KPIDriversDisplay
            className={`${baseClassName}__stack`}
            datasetSource={dataset}
            driverOptions={driverOptionsByDataset[dataset]}
            renderMode='dimensionSelect'
            hideSwitches
            excludeDimensions={[KpiDimension.REPORTING_CURRENCY]}
            onChange={handleChangeFor(dataset)}
          />
          {canDeleteSource && (
            <Box className={`${baseClassName}__delete-row`}>
              <button
                type='button'
                className={`${baseClassName}__delete-source`}
                onClick={() => handleDeleteSource(dataset)}
              >
                Delete Source from Page Driver
              </button>
            </Box>
          )}
        </Box>
      ))}

      <Menu anchorEl={addMenuAnchor} open={Boolean(addMenuAnchor)} onClose={closeAddMenu}>
        {addableDatasets.map((dataset) => (
          <MenuItem key={dataset} onClick={() => handleAddSource(dataset)}>
            {resolveDatasetSourceLabel(dataset)}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default PageDriverStep;
